import { useQuery } from "@apollo/client";

import { useIntl } from "react-intl";

import {EMPLOYEE_MANY, EMPLOYEE_MANY_GAPS} from "../../schema/employee";
import  List  from "../employees/List";

import { Loading } from "../ui/Alerts";
import {Competence, Employee, SkillType} from "../../types";
import Item from "../employees/Item";
import React, {useState} from "react";
import {Link} from "../ui/permissions";
import {SKILL_TYPE_MANY} from "../../schema/skillType";

const DashboardSkillGaps = () => {

    const {loading, data: employees} = useQuery(EMPLOYEE_MANY);

    const { data: skillTypes } = useQuery(SKILL_TYPE_MANY);
    const [skillFilter, setskillFilter] = useState("");
    const [belowFilter, setBelowFilter] = useState(100);


    const filteredEmployees = employees?.employeeMany
        ?.filter((employee: Employee) => {
            if (!skillFilter) return true;

            const hasMatchingCompetence =
                employee.competences?.some(
                    (c) => c.skill?.skillType?._id === skillFilter
                ) ||
                employee.job?.competences?.some(
                    (c: Competence) => c.skill?.skillType?._id === skillFilter
                );

            return hasMatchingCompetence;
        })
        .map((employee: Employee) => {
            const filteredEmployeeCompetences = skillFilter
                ? employee.competences?.filter(
                    (c) => c.skill?.skillType?._id === skillFilter
                )
                : employee.competences;

            const filteredJobCompetences = skillFilter
                ? employee.job?.competences?.filter(
                    (c:Competence) => c.skill?.skillType?._id === skillFilter
                )
                : employee.job?.competences;

            return {
                ...employee,
                competences: filteredEmployeeCompetences,
                job: {
                    ...employee.job,
                    competences: filteredJobCompetences,
                },
            };
        });

    const skillGaps = calculateEmployeeSkillGaps(filteredEmployees);


    const createTypeFilter = () => {
        return (
            <div className="space-y-1">
            <label
                htmlFor="type"
                className="block text-sm font-medium text-gray-700"
            > Kompetenztyp </label>
            <select
                id="type"
                autoComplete="type"
                onChange={(e) => setskillFilter(e.target.value)}
                className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            >
                <option value=""></option>
                {skillTypes?.skillTypeMany.map((skillType: SkillType) => (
                    <option key={skillType._id} value={skillType._id}>
                        {skillType.name}
                    </option>
                ))}
            </select>
            </div>
        );
    };

    const createThresholdFilter = () => {
        return(
            <div className="space-y-1">
                <label
                    htmlFor="level"
                    className="block text-sm font-medium text-gray-700"
                >
                    Abdeckungsgrad kleiner als (%)
                </label>
                <input
                    onChange={(e) => setBelowFilter(e.target.valueAsNumber)}
                    type="number"
                    id="level"
                    min={0}
                    max={100}
                    defaultValue={100}
                    className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
            </div>
        );
    };

    interface SkillGap {
        skillId: string;
        skillName: string;
        userValue: number;
        jobValue: number;
        gap: number;
    }

    interface EmployeeSkillGap {
        employee: Employee;
        skillGaps: SkillGap[];
        normalizedGap: number;
    }

    function calculateEmployeeSkillGaps(employees:Employee[] ): EmployeeSkillGap[] {
        if (employees == null) {return []}

        return employees.map((employee: Employee): EmployeeSkillGap => {
            const userSkills = employee.competences;
            const jobSkills = employee.job?.competences || [];

            const userSkillMap = new Map<string, number>(
                userSkills.map((comp: Competence) => [comp.skillId, comp.value])
            );

            const jobSkillMap = new Map<string, number>(
                jobSkills.map((comp: Competence) => [comp.skillId, comp.value])
            );

            const allSkillIds = new Set<string>();

            Array.from(userSkillMap.keys()).forEach(key => allSkillIds.add(key));
            Array.from(jobSkillMap.keys()).forEach(key => allSkillIds.add(key));

            const skillGaps: SkillGap[] = Array.from(allSkillIds).map((skillId) => {
                const userComp = userSkills.find((c) => c.skillId === skillId);
                const jobComp = jobSkills.find((c: Competence) => c.skillId === skillId);

                const userValue = userSkillMap.get(skillId) || 0;
                const jobValue = jobSkillMap.get(skillId) || 0;
                const skillName = userComp?.skill?.name || jobComp?.skill?.name || "Unknown";

                const gap = Math.max(jobValue - userValue, 0);

                return {
                    skillId,
                    skillName,
                    userValue,
                    jobValue,
                    gap,
                };
            });

            const relevantGaps = skillGaps.filter(g => g.jobValue > g.userValue);

            const totalGap = relevantGaps.reduce((sum, g) => sum + g.gap, 0);
            const maxPossibleGap = relevantGaps.reduce((sum, g) => sum + g.jobValue, 0);

            const normalizedGap = Math.ceil(maxPossibleGap > 0 ? (totalGap / maxPossibleGap) * 100 : 0);

            return {
                employee,
                skillGaps,
                normalizedGap,
            };
        });
    }

    const thresholdFilter =  skillGaps?.filter((val) => {
        return (100-val.normalizedGap) <= belowFilter;
    });



    return (
        <>
            {loading && <Loading />}


            <div className="grid grid-cols-2 gap-2 mb-6">
                {createTypeFilter()}
                {createThresholdFilter()}
            </div>




            <h3 className="text-lg leading-6 font-medium text-gray-900">
                Kompetenzlücken
            </h3>


            <table>
                <thead className="bg-gray-100">
                    <th
                        scope="col"
                        className="w-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >Mitarbeiter</th>
                    <th
                        scope="col"
                        className="w-24 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >Kompetenzabdeckung</th>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {thresholdFilter?.map((employeeSkillGap: EmployeeSkillGap) => (
                    <tr>
                        <td className="px-6 py-4">
                            <div className="flex items-center">
                                <Link to={`/employees/${employeeSkillGap.employee._id}/details`}>
                                    <h3 className="text-gray-900 text-sm leading-5 font-medium truncate">
                                        {employeeSkillGap.employee.firstName} {employeeSkillGap.employee.lastName}
                                    </h3>
                                </Link>
                            </div>
                        </td>
                        <td className="px-6 py-4">
                            <div className="flex items-center">
                                <Link to={`/employees/${employeeSkillGap.employee._id}/details`}>
                                    <h3 className="text-gray-900 text-sm leading-5 font-medium truncate">
                                        {100 - employeeSkillGap.normalizedGap}%
                                    </h3>
                                </Link>
                            </div>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

        </>
    );
};



export default DashboardSkillGaps;
